<template>
  <div class="content">

    <!-- Titled as FrameTracker titles its window, with the menu path above it. -->
    <div class="card card-outline card-primary no-print">
      <div class="card-header">
        <div class="row align-items-center">
          <div class="col">
            <p class="text-muted mb-0 small">
              Reports &rsaquo; {{ definition ? definition.groupLabel : '' }}
            </p>
            <h2 class="mb-0">{{ title }}</h2>
          </div>
          <div class="col-auto">
            <router-link :to="{ name: 'Reports' }" class="btn btn-default">
              Back to Reports
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div v-if="notFound" class="card card-outline card-danger no-print">
      <div class="card-body">
        No report called &ldquo;{{ slug }}&rdquo; is available to your account.
      </div>
    </div>

    <div v-else-if="!definition" class="card card-outline no-print">
      <div class="card-body text-muted">Loading&hellip;</div>
    </div>

    <template v-else>

      <!--
        About this report: the server's plain-language notes (how it was checked
        against FrameTracker, oddities kept on purpose, deliberate differences and
        open questions), grouped under a heading per kind.
      -->
      <div v-if="noteGroups.length" class="card card-outline card-info no-print">
        <div class="card-header">
          <h3 class="card-title">About this report</h3>
          <div class="card-tools">
            <button type="button" class="btn btn-tool" @click="showNotes = !showNotes">
              {{ showNotes ? 'Hide' : 'Show' }}
            </button>
          </div>
        </div>
        <div class="card-body" v-show="showNotes">
          <div v-for="group in noteGroups" :key="group.kind" class="report-notes-group">
            <h6 class="report-notes-heading" :class="'text-' + group.tone">{{ group.heading }}</h6>
            <ul class="mb-0">
              <li v-for="(text, i) in group.texts" :key="i">{{ text }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!--
        Parameter form. Fields, labels, order and control types all come from the
        server's schema, which mirrors the legacy dialog (rptInvDaily.dfm), including
        the radio group for report order rather than a dropdown. A param can be
        shown, enabled or demanded depending on another (visibleWhen, enabledWhen,
        requiredWhen), as FrameTracker's OnClick handlers do.
      -->
      <div class="card card-outline no-print">
        <div class="card-body">
          <div class="row">
            <template v-for="param in scalarParams" :key="param.name">
              <div class="col-md-3" v-if="isVisible(param, form)">
                <div class="form-group">
                  <label :for="'p_' + param.name">{{ param.label }}</label>

                  <!--
                    Lookups: every param with a server list or inline options, so a
                    salesman, a location or a product type all pick the same way.
                    Blank means "all", as the legacy form's own label says.
                  -->
                  <Multiselect
                      v-if="isPicker(param)"
                      :id="'p_' + param.name"
                      class="report-picker"
                      :class="{'report-upper': param.uppercase}"
                      v-model="form[param.name]"
                      :mode="param.multiple ? 'tags' : 'single'"
                      :options="param.search ? searchers[param.name] : pickerOptions[param.name]"
                      :searchable="true"
                      :can-clear="true"
                      :can-deselect="true"
                      :close-on-select="!param.multiple"
                      :create-option="!!param.allowCustom"
                      :object="!!param.search"
                      :allow-absent="true"
                      :delay="param.search ? 300 : -1"
                      :min-chars="param.search ? 2 : 0"
                      :resolve-on-load="false"
                      :filter-results="!param.search"
                      :clear-on-search="false"
                      :limit="param.search ? 50 : -1"
                      :disabled="param.readOnly || !enabled(param)"
                      :placeholder="param.placeholder !== undefined && param.placeholder !== null ? param.placeholder : '[ Blank for all ]'"
                      :no-options-text="param.search ? 'Type at least 2 characters' : 'No options'"
                      no-results-text="No matches"
                  />

                  <!-- TCheckBox: sends checkedValue or uncheckedValue. -->
                  <div class="form-check report-check" v-else-if="param.type === 'checkbox'">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'p_' + param.name"
                        :true-value="String(param.checkedValue)"
                        :false-value="String(param.uncheckedValue !== undefined ? param.uncheckedValue : '')"
                        :disabled="param.readOnly || !enabled(param)"
                        v-model="form[param.name]">
                  </div>

                  <input
                      v-else-if="param.type === 'date'"
                      :id="'p_' + param.name"
                      type="date"
                      class="form-control"
                      :readonly="param.readOnly"
                      :disabled="!enabled(param)"
                      v-model="form[param.name]">

                  <input
                      v-else-if="param.type === 'number'"
                      :id="'p_' + param.name"
                      type="number"
                      class="form-control"
                      :readonly="param.readOnly"
                      :disabled="!enabled(param)"
                      :placeholder="param.placeholder || ''"
                      v-model="form[param.name]">

                  <!-- CharCase = ecUpperCase: shown in capitals, stored in capitals. -->
                  <input
                      v-else
                      :id="'p_' + param.name"
                      type="text"
                      class="form-control"
                      :class="{'report-upper': param.uppercase}"
                      :readonly="param.readOnly"
                      :disabled="!enabled(param)"
                      :placeholder="param.placeholder || ''"
                      v-model="form[param.name]"
                      @change="upper(param)">
                </div>
              </div>
            </template>

            <!-- formReadouts: read-only values FrameTracker shows on the form. -->
            <div class="col-md-3" v-for="(readout, r) in readouts" :key="'ro' + r">
              <div class="form-group">
                <label>{{ readout.label }}</label>
                <input type="text" class="form-control" readonly :value="readout.text">
              </div>
            </div>
          </div>

          <!--
            Radio groups get their own row so the multi-column layout survives.
            rgOrder is declared Columns = 2 (rptInvDaily.dfm:200), and matching that
            is most of what makes this form recognizable to an operator.
          -->
          <template v-for="param in radioParams" :key="param.name">
            <div class="row" v-if="isVisible(param, form)">
              <div class="col-md-6">
                <fieldset class="form-group border rounded p-2" :disabled="param.readOnly || !enabled(param)">
                  <legend class="col-form-label small text-muted w-auto px-1 mb-0">
                    {{ param.label }}
                  </legend>
                  <div class="row">
                    <div
                        :class="'col-' + (12 / (param.columns || 1))"
                        v-for="opt in optionsFor(param)"
                        :key="opt.value">
                      <div class="form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            :id="'p_' + param.name + '_' + opt.value"
                            :value="opt.value"
                            v-model="form[param.name]">
                        <label class="form-check-label" :for="'p_' + param.name + '_' + opt.value">
                          {{ opt.label }}
                        </label>
                      </div>
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
          </template>
        </div>

        <div class="card-footer">
          <button class="btn btn-primary" :disabled="running || !definition.implemented" @click="run()">
            {{ running ? 'Running…' : runLabel }}
          </button>

          <!-- extraRuns: another run button with its own params (usedBy). -->
          <span class="ml-3 d-inline-flex align-items-center" v-for="extra in extraRuns" :key="extra.mode">
            <template v-for="param in usedByParams(extra.mode)" :key="param.name">
              <label class="mb-0 mr-1 small" :for="'p_' + param.name">{{ param.label }}</label>
              <input
                  :id="'p_' + param.name"
                  :type="param.type === 'date' ? 'date' : (param.type === 'number' ? 'number' : 'text')"
                  class="form-control form-control-sm mr-2 ft-inline-input"
                  :readonly="param.readOnly"
                  v-model="form[param.name]">
            </template>
            <button class="btn btn-default" :disabled="running || !definition.implemented" @click="run(extra.mode)">{{ extra.label }}</button>
          </span>

          <span v-if="!definition.implemented" class="text-muted ml-2">
            This report has not been migrated from FrameTracker yet.
          </span>

          <!-- FrameTracker's warning when a required control is blank. -->
          <span v-if="formError" class="text-danger ml-2">{{ formError }}</span>

          <div class="btn-group float-right" v-if="hasResult">
            <button
                class="btn"
                :class="view === 'report' ? 'btn-primary' : 'btn-default'"
                v-if="layout"
                @click="setView('report')">Report</button>
            <button
                class="btn"
                :class="view === 'grid' ? 'btn-primary' : 'btn-default'"
                @click="setView('grid')">Grid</button>
          </div>
        </div>
      </div>

      <div v-if="error" class="card card-outline card-danger no-print">
        <div class="card-body">{{ error }}</div>
      </div>

      <!--
        FrameTracker's MessageDlg when the query finds nothing ('No Information
        Found', 'No Orders Found' ...). A report that showed none declares none.
      -->
      <div v-if="emptyMessage" class="card card-outline card-warning no-print">
        <div class="card-body">{{ emptyMessage }}</div>
      </div>

      <!--
        Grid view: every column the report returns, sortable, with DataTables'
        copy/CSV/Excel/PDF/print buttons. This is the functional view; the export
        path lives here. Where the report declares FrameTracker's own export, the
        CSV and Excel buttons write exactly that (see exportFile).
      -->
      <div class="card card-outline no-print" v-show="hasResult && view === 'grid'">
        <div class="card-body">
          <table :id="tableId" class="table table-bordered table-striped table-sm" style="width:100%">
            <thead>
            <tr>
              <th v-for="col in gridColumns" :key="col.key">{{ col.label }}</th>
            </tr>
            </thead>
            <tbody></tbody>
          </table>
        </div>
      </div>

      <!--
        Report view: the printed FrameTracker layout: its column subset, its
        grouping, its totals, its headings and formats. Same response as the grid,
        rendered the way the people who have used this system for years expect.
      -->
      <div class="card card-outline ft-report-card" v-if="showReport">

        <div class="card-header ft-toolbar no-print">
          <button class="btn btn-sm btn-default" @click="printReport">Print this report</button>
          <button class="btn btn-sm btn-default ml-1" @click="exportReportCsv"
                  :disabled="running || !rows.length">Export CSV</button>
          <span class="text-muted small ml-2" v-if="sort.key && !special">
            Sorted by {{ sortLabel }}. Groups and subtotals are off while sorted;
            <a href="#" @click.prevent="sort = {key: null, dir: null}">back to FrameTracker's order</a>.
          </span>
          <span class="text-muted small ml-2" v-if="pageInfo.pages > 1 && !special">
            Prints every page, not only the one on screen.
          </span>
        </div>

        <!-- Page header items on screen; in print the page margin boxes draw them. -->
        <div class="ft-page-strip no-print" v-if="pageItems.length && !special">
          <span>{{ pageItemText('topLeft') }}</span>
          <span>{{ pageItemText('topCenter') }}</span>
          <span :class="pageItemClass('topRight')">{{ pageItemText('topRight') }}</span>
        </div>

        <!-- A layout of its own: the Delivery Log driver sheet. -->
        <div class="card-body ft-report ft-special" v-if="special === 'deliverySheet'">
          <DeliverySheet
              v-if="layout.sheet"
              :rows="rows"
              :sheet="layout.sheet"
              :heading="deliveryHeading"
              :company="pageItems.some(i => i.key === 'company') ? company : ''"
              :render="renderForRow"
              :footer-left="pageItemText('bottomLeft')"
              :footer-right="pageItemText('bottomRight')"/>
          <p v-else class="text-danger">This layout is missing its sheet description.</p>
        </div>

        <!--
          The printed layout: one ReportTable, or several printed one after
          another in a single run (`sections`, Check Register "Both").
        -->
        <template v-else>
          <ReportTable
              v-for="(section, s) in tableSections"
              :key="s"
              :layout="section.layout"
              :rows="section.rows"
              :columns="section.columns"
              :context="templateContext"
              :meta="meta || {}"
              :ran-with="ranWith"
              :print-all="printAll"
              :sortable="sortable"
              :sort-key="sort.key"
              :sort-dir="sort.dir"
              @sort="onSort"
              @pages="s === 0 ? onPages($event) : null"/>
        </template>

        <!-- Page footer items on screen (User:, Page n of N). -->
        <div class="ft-page-strip ft-page-foot no-print" v-if="pageItems.length && !special">
          <span>{{ pageItemText('bottomLeft') }}</span>
          <span>{{ pageItemText('bottomCenter') }}</span>
          <span>{{ pageItemText('bottomRight') }}</span>
        </div>
      </div>

    </template>

  </div>
</template>

<script>
import $ from "jquery";
import {mapGetters} from "vuex";
import Multiselect from '@vueform/multiselect'
import JSZip from 'admin-lte/plugins/jszip/jszip.min.js'
import DeliverySheet from './DeliverySheet.vue'
import ReportTable from './ReportTable.vue'
import {NUMERIC_TYPES, formatCell} from './reportCells'
import {conditionHolds, defaultFor, isLocked, isPicker, isVisible, missingRequired, serializeForm} from './reportParams'
import {dateToStr, formatDateTime, formatWith} from './delphiFormat'
import {partApplies, pathValue, renderLines, renderTemplate} from './reportTemplate'
import {nextSort, sortRows, sortedLayout} from './reportSort'
import {buildExportTable, download, neutralize, toCsv, toXlsx} from './reportExport'
import {clearPrintPage, setPrintPage} from './printPage'

/**
 * Runs any report in the catalog.
 *
 * One component serves every report: the parameter form, the result grid and the
 * FrameTracker-lookalike view are all driven by the server's definition, so porting
 * the next report is backend work. A report needing a bespoke screen gets its own
 * component through `legacyLayout.special` (DeliverySheet is the first).
 *
 * Everything the Report view decides from a parameter (grouped or flat, which
 * columns, whether the total prints, the heading) reads `ranWith`, the criteria
 * of the run on screen, never the live form: editing the form after a run must
 * not redraw a result already printed.
 */

// Headings for the server's note kinds (ReportDefinition::NOTE_KINDS), in display order.
const NOTE_GROUPS = [
  {kind: 'question', heading: 'Question for you', tone: 'danger'},
  {kind: 'verified', heading: 'How we checked it', tone: 'success'},
  {kind: 'kept', heading: 'Kept as FrameTracker does it', tone: 'secondary'},
  {kind: 'changed', heading: 'Different from FrameTracker', tone: 'primary'},
]

// Printing every page of a result this long draws it all at once; ask first.
const PRINT_CONFIRM_LINES = 5000


// `pageHeader: true`: every item FrameTracker's page bands usually carry.
// ReportBuilder's vtDate prints m/d/yyyy unless given a DisplayFormat.
const PAGE_HEADER_DEFAULTS = {company: true, user: true, printDate: 'm/d/yyyy', pageNumbers: 'Page n of N'}

export default {
  name: "ReportRunner",
  components: {
    Multiselect,
    DeliverySheet,
    ReportTable,
  },
  data() {
    return {
      form: {},
      running: false,
      // Defaults to the familiar view where one exists; the grid is a click away
      // and is where the export buttons live.
      view: 'report',
      notFound: false,
      tableBuilt: false,
      showNotes: true,
      // The criteria the result on screen was run with (as sent), and the option
      // objects chosen for them, for {param:label} in headings. See the note at
      // the top of this component.
      ranWith: {},
      ranOptions: {},
      // When the result on screen came back: FrameTracker's print date, and the
      // sign that a run has finished (for the empty-result message).
      ranAt: null,
      // The first table's page on screen and page count (ReportTable pages
      // itself), for the screen's "Page n of N".
      pageInfo: {page: 0, pages: 1},
      // True while printing: the Report view draws every page, not one.
      printAll: false,
      // Whole lookup lists by source name, filled by loadLookups().
      lookups: {},
      // The first required-but-blank message, as FrameTracker's MessageDlg.
      formError: '',
      // formReadouts' rows by source.
      readoutRows: {},
      // Multiple picks as last seen, for exclusiveOption.
      lastPicks: {},
      // Params locked on the user's own value (lockWhenDefaulted), by name.
      locked: {},
      // Report view column sort ({key, dir}); off means FrameTracker's order.
      sort: {key: null, dir: null},
    }
  },
  computed: {
    ...mapGetters({
      'definitions': 'Reports/definitionBySlug',
      'rows': 'Reports/getRows',
      'meta': 'Reports/getMeta',
      'error': 'Reports/getError',
      'catalogMeta': 'Reports/getCatalogMeta',
      'userInfo': 'userAccess/user',
    }),
    slug() {
      return this.$route.params.slug
    },
    definition() {
      return this.definitions[this.slug] || null
    },
    params() {
      return (this.definition && this.definition.params) || []
    },
    paramDefs() {
      const map = {}
      this.params.forEach(p => {
        map[p.name] = p
      })
      return map
    },
    // The open question goes first: it is the one note that asks the reader to act.
    noteGroups() {
      const notes = (this.definition && this.definition.notes) || []
      return NOTE_GROUPS
        .map(g => ({...g, texts: notes.filter(n => n.kind === g.kind).map(n => n.text)}))
        .filter(g => g.texts.length)
    },
    /**
     * The printed layout for what is currently on screen.
     *
     * A report whose result SHAPE depends on a parameter (rptCheckRegister, whose
     * radio groups select between six queries returning different column sets)
     * states the layout it actually produced in the response. That is
     * authoritative: the catalog entry can only describe the default. It also
     * holds for an empty run, so a variant's emptyMessage is the one shown.
     *
     * Before a run there is no response, so the catalog's own entry stands. That is
     * what decides whether the Report button appears at all.
     */
    layout() {
      if (this.ranAt && this.meta && this.meta.legacyLayout) {
        return this.meta.legacyLayout
      }
      // variantSelect: the first variant whose condition holds for the run
      // (the server picks the same); used when the response names no layout.
      const def = this.definition
      if (this.ranAt && def && Array.isArray(def.variantSelect) && def.variants) {
        const pick = def.variantSelect.find(v => partApplies(v, this.ranWith, this.meta || {}, this.paramDefs))
        const variant = pick && def.variants[pick.variant]
        if (variant && variant.legacyLayout) return variant.legacyLayout
      }
      return (def && def.legacyLayout) || null
    },
    /** The run button's caption: FrameTracker's when it differs ('Run'). */
    runLabel() {
      return (this.definition && this.definition.runLabel) || (this.layout && this.layout.runLabel) || 'Print'
    },
    /**
     * Whether the Report view shows: a result, or an empty run of a report
     * that has no "nothing found" message, which FrameTracker prints as its
     * headings over no rows.
     */
    showReport() {
      if (this.view !== 'report' || !this.layout) return false
      if (this.hasResult) return true
      return !!this.ranAt && !this.error && !this.layout.emptyMessage
    },
    title() {
      if (this.layout && this.layout.title) return this.layout.title
      return this.definition ? this.definition.label : ''
    },
    hasResult() {
      return this.rows && this.rows.length > 0
    },
    /** FrameTracker's "nothing found" message, once a run has come back empty. */
    emptyMessage() {
      if (!this.ranAt || this.hasResult || this.error || !this.layout) return ''
      return this.layout.emptyMessage || ''
    },
    // Params belonging to an extra run button (usedBy) sit beside that button.
    scalarParams() {
      return this.params.filter(p => p.type !== 'radio' && !p.usedBy)
    },
    /** extraRuns: further run buttons, each sending `mode` (History Print). */
    extraRuns() {
      return (this.definition && this.definition.extraRuns) || []
    },
    /**
     * formReadouts: read-only values on the form (FrameTracker's First Date /
     * # of Records), each a field of the one row its options source returns.
     */
    readouts() {
      return ((this.definition && this.definition.formReadouts) || []).map(r => {
        const row = (this.readoutRows[r.source] || [])[0] || {}
        const value = row[r.field]
        let text = value === undefined || value === null ? '' : String(value)
        if (text !== '' && r.format) text = formatWith(r.format, value)
        else if (/^\d{4}-\d{2}-\d{2}/.test(text)) text = dateToStr(text)
        return {label: r.label, text}
      })
    },
    radioParams() {
      return this.params.filter(p => p.type === 'radio')
    },
    /**
     * Each picker's option list, by param name. Built here rather than by calling
     * optionsFor() in the template, which would hand the multiselect a new array
     * on every render and make it re-resolve its labels each time.
     */
    pickerOptions() {
      const map = {}
      this.scalarParams.forEach(p => {
        if (isPicker(p) && !p.search) map[p.name] = this.optionsFor(p)
      })
      return map
    },
    /**
     * Search pickers ask the server as the user types (the multiselect debounces
     * and enforces the 2-character minimum). One stable function per param.
     */
    searchers() {
      const map = {}
      this.scalarParams.forEach(p => {
        if (isPicker(p) && p.search) {
          map[p.name] = q => this.$store.dispatch('Reports/fetchOptions', {source: p.optionsSource, q})
              .then(list => this.labeled(p, list))
        }
      })
      return map
    },
    /** Sys.Company, from the catalog. */
    company() {
      return (this.catalogMeta && this.catalogMeta.company) || ''
    },
    /** The signed-in user's login, for FrameTracker's "User:" footer. */
    userLogin() {
      const user = this.userInfo || {}
      return user.username || user.email || user.name || ''
    },
    /**
     * Columns for what is currently on screen, in order of authority:
     *
     *   1. What the response said it returned. A variant report returns a different
     *      column set per run, and the catalog entry describes only the default;
     *      using it would key every cell of the other variants against the wrong
     *      name and render a grid of blanks.
     *   2. What the catalog declared.
     *   3. The keys of the first row, for a report predating the catalog and served
     *      by its own endpoint. This is what the previous Reports page did for all
     *      of them.
     */
    gridColumns() {
      if (this.hasResult && this.meta && (this.meta.columns || []).length > 0) {
        return this.meta.columns
      }
      const declared = (this.definition && this.definition.columns) || []
      if (declared.length > 0) return declared
      if (!this.hasResult) return []
      return Object.keys(this.rows[0]).map(k => ({key: k, label: k, type: 'string'}))
    },
    columnsByKey() {
      const map = {}
      this.gridColumns.forEach(c => {
        map[c.key] = c
      })
      return map
    },
    /**
     * Whether this report prints as a label/value stack rather than a table.
     *
     * A report with four scalars and no dataset (rptInvtSum is the first) has
     * nothing the table path can use: no columns, no grouping, no totals. The
     * server says so explicitly rather than this being inferred from a row count,
     * because one row is a perfectly ordinary result for a table report too.
     */
    summary() {
      return !!this.layout && this.layout.style === 'summary'
    },
    /** A layout drawn by its own component ('deliverySheet'). */
    special() {
      return (this.layout && this.layout.special) || null
    },
    /** The template context for the run on screen. */
    templateContext() {
      return {
        params: this.ranWith,
        paramDefs: this.paramDefs,
        options: this.ranOptions,
        meta: this.meta || {},
        columns: this.columnsByKey,
        rows: this.rows,
        user: this.userLogin,
      }
    },
    /**
     * What the Report view prints: the layout over the rows, or each of its
     * `sections` in turn, a section reading its own rows from meta (`source`)
     * with its own `columnDefs`.
     */
    tableSections() {
      if (!this.layout) return []
      const sections = Array.isArray(this.layout.sections) && this.layout.sections.length ? this.layout.sections : null
      const sorted = !!this.sort.key
      const shape = (layout, rows, columns) => {
        if (!sorted) return {layout, rows, columns}
        const types = {}
        columns.forEach(c => { types[c.key] = c })
        return {layout: sortedLayout(layout), rows: sortRows(rows, this.sort, types), columns}
      }
      if (!sections) return [shape(this.layout, this.rows, this.gridColumns)]
      return sections.map(section => {
        const rows = section.source ? pathValue({meta: this.meta || {}}, section.source) : this.rows
        return shape(section, Array.isArray(rows) ? rows : [], section.columnDefs || this.gridColumns)
      })
    },
    /** Header clicks sort the printed layouts; the special ones draw their own. */
    sortable() {
      return !!this.layout && !this.special && !this.summary
    },
    /** The rows in the order on screen: sorted, or FrameTracker's. */
    displayRows() {
      return this.sort.key ? sortRows(this.rows, this.sort, this.columnsByKey) : this.rows
    },
    sortLabel() {
      const col = this.columnsByKey[this.sort.key]
      return (col && col.label) || this.sort.key
    },
    /** The run-time heading (lblHead), or null where the layout declares none. */
    headingText() {
      if (!this.layout || this.layout.heading === undefined || this.layout.heading === null) return null
      return renderTemplate(this.layout.heading, this.templateContext)
    },
    /** The Delivery Log's lblHead: the declared heading, else FrameTracker's text. */
    deliveryHeading() {
      if (this.headingText !== null) return this.headingText
      return 'Delivery Log for Location: ' + this.paramText('loc', 'label')
    },
    tableId() {
      return 'report_' + this.slug
    },
    /**
     * FrameTracker's page-band items (pageHeader): company name, User:, print
     * date, page numbers, each at a page corner. `true` takes them all at their
     * usual places; an item may be `true`, a format string, or {format, label,
     * at, bold, italic, size}. `userLabel` ('User:', 'Username:') and
     * `printDateLabel` (verbatim, typos included) set the text before those two
     * items; `printDatePosition: 'footer'` moves the date to the page foot. Corners: topLeft, topCenter, topRight, bottomLeft,
     * bottomCenter, bottomRight.
     */
    pageItems() {
      const spec = this.layout && this.layout.pageHeader
      if (!spec) return []
      const ph = spec === true ? PAGE_HEADER_DEFAULTS : spec
      const item = (value, defaults) => {
        if (!value) return null
        if (typeof value === 'object') return {...defaults, ...value}
        if (typeof value === 'string') return {...defaults, format: value}
        return {...defaults}
      }
      const items = []
      const printedAt = this.ranAt || new Date()

      const company = item(ph.company, {at: 'topRight', bold: true, italic: true, size: 11})
      if (company) items.push({...company, key: 'company', text: this.company})

      const printDate = item(ph.printDate, {
        at: ph.printDatePosition === 'footer' ? 'bottomLeft' : 'topLeft',
        format: 'm/d/yyyy',
        label: ph.printDateLabel || '',
      })
      if (printDate) {
        const date = formatDateTime(printDate.format, printedAt)
        items.push({...printDate, text: printDate.label ? printDate.label + ' ' + date : date})
      }

      const user = item(ph.user, {at: 'bottomLeft', label: ph.userLabel !== undefined ? ph.userLabel : 'User:'})
      if (user) items.push({...user, text: (user.label ? user.label + ' ' : '') + this.userLogin})

      const pages = item(ph.pageNumbers, {at: 'bottomRight', format: 'Page n of N'})
      if (pages) items.push({...pages, pages: pages.format})

      return items
    },
    /** What the printed page should carry, for the view on screen. */
    printSpec() {
      if (!this.showReport) return null
      const orientation = (this.definition && this.definition.orientation) || this.layout.orientation
      const size = orientation === 'landscape' ? 'letter landscape' : 'letter'
      const boxes = {}
      // The driver sheet draws Sys.Company in its own header band.
      this.pageItems.filter(it => !(this.special === 'deliverySheet' && it.key === 'company')).forEach(it => {
        boxes[it.at] = it.pages !== undefined
            ? {pages: it.pages, bold: it.bold, italic: it.italic, size: it.size}
            : {text: it.text, bold: it.bold, italic: it.italic, size: it.size}
      })
      if (this.special === 'deliverySheet') {
        // Quarter-inch margins, the footer band's 13.2 mm at the foot.
        return {size, margin: '6.35mm 6.35mm 13.229mm 6.35mm', boxes}
      }
      return {size, margin: '12mm', boxes}
    },
  },
  methods: {
    /**
     * A parameter's options, minus any that do not apply to the rest of the form.
     *
     * An option may declare the combination it belongs to. rptCheckRegister's
     * 'CC Summary' layout only exists for the Charge Card report: picking it
     * alongside Check Register matches no branch in the original and prints
     * nothing at all, so it is hidden rather than reproduced.
     *
     * Every server list, FrameTracker's Location table included, comes from the
     * options endpoint; see loadLookups().
     */
    optionsFor(param) {
      if (param.options) {
        return param.options.filter(opt => {
          const when = opt.when || {}
          return Object.keys(when).every(k => this.form[k] === when[k])
        })
      }
      if (param.optionsSource) return this.labeled(param, this.lookups[param.optionsSource] || [])
      return []
    },
    isPicker,
    isVisible,
    /**
     * Enabled by enabledWhen and not locked on the user's own value (readOnly is
     * handled by each control). A disabled param is still sent.
     */
    enabled(param) {
      return !this.locked[param.name] && conditionHolds(param.enabledWhen, this.form)
    },
    /** CharCase = ecUpperCase: what was typed is kept in capitals. */
    upper(param) {
      const value = this.form[param.name]
      if (param.uppercase && typeof value === 'string' && value !== value.toUpperCase()) {
        this.form[param.name] = value.toUpperCase()
      }
    },
    resetForm() {
      const context = {user: (this.catalogMeta && this.catalogMeta.user) || {}}
      const form = {}
      const locked = {}
      this.params.forEach(p => {
        form[p.name] = defaultFor(p, context)
        if (isLocked(p, context)) locked[p.name] = true
      })
      this.form = form
      this.locked = locked
    },
    /**
     * Fetches the whole lists this report's pickers need. The store caches each
     * source for the session, so reopening a report or two params sharing a
     * source cost one request. Search sources load as the user types instead.
     */
    loadLookups() {
      const sources = new Set(this.params
          .filter(p => p.optionsSource && !p.search)
          .map(p => p.optionsSource))

      sources.forEach(source => {
        if (this.lookups[source]) return
        this.$store.dispatch('Reports/fetchOptions', {source})
            .then(list => {
              this.lookups = {...this.lookups, [source]: list}
            })
      })
    },
    /**
     * The option object(s) behind each value sent, kept with the run so a
     * heading can print {loc:label} even after the form changes. A typed value
     * outside the list stands for itself.
     */
    chosenOptions(sent) {
      const out = {}
      Object.keys(sent).forEach(name => {
        const param = this.paramDefs[name]
        if (!param) return
        const held = this.form[name]
        const list = param.type === 'radio' || param.options
            ? (param.options || [])
            : (isPicker(param) && !param.search ? (this.lookups[param.optionsSource] || []) : [])
        const find = v => {
          if (v !== null && typeof v === 'object') return v
          return list.find(o => String(o.value) === String(v)) || {value: v, label: String(v)}
        }
        out[name] = Array.isArray(held) ? held.map(find) : find(held)
      })
      return out
    },
    /**
     * Whether a hideTotalsWhen condition holds for the run on screen: any key
     * set ('*') or equal to its value. Keys may be reserved names
     * ('_scope:sls', '_meta:key').
     */
    hiddenBy(when) {
      if (!when) return false
      return Object.keys(when).some(k => {
        let value = this.ranWith[k]
        if (k.indexOf('_scope:') === 0) value = pathValue(this.meta || {}, 'scope.' + k.slice(7))
        else if (k.indexOf('_meta:') === 0) value = pathValue(this.meta || {}, k.slice(6))
        const set = value !== undefined && value !== null && value !== ''
        return when[k] === '*' ? set : set && String(value) === String(when[k])
      })
    },
    /** Whether the run on screen sent `value` for `name` (strings compared). */
    ranMatches(name, value) {
      const sent = this.ranWith[name]
      if (sent === undefined || sent === null) return value === '' || value === null
      return String(sent) === String(value)
    },
    /**
     * optionLabel: how a picker shows each option, a template over the option's
     * fields copying FrameTracker's lookup columns ('{Company}  {State}
     * {Vendnum}'). The value is untouched.
     */
    labeled(param, list) {
      if (!param.optionLabel || !Array.isArray(list)) return list
      return list.map(o => ({
        ...o,
        label: String(param.optionLabel).replace(/\{([A-Za-z0-9_]+)\}/g, (m, f) => (o[f] === undefined || o[f] === null ? '' : String(o[f]))),
      }))
    },
    loadReadouts() {
      ((this.definition && this.definition.formReadouts) || []).forEach(r => {
        this.$store.dispatch('Reports/fetchOptions', {source: r.source, fresh: true})
            .then(list => {
              this.readoutRows = {...this.readoutRows, [r.source]: list}
            })
      })
    },
    /**
     * exclusiveOption on a multiple pick (FrameTracker's All of Above): picking
     * it clears the others; picking another clears it.
     */
    applyExclusive() {
      this.params.forEach(p => {
        if (!p.multiple || p.exclusiveOption === undefined) return
        const ex = String(p.exclusiveOption)
        const cur = (Array.isArray(this.form[p.name]) ? this.form[p.name] : []).map(String)
        const prev = this.lastPicks[p.name] || []
        let next = cur
        const added = cur.filter(v => !prev.includes(v))
        if (added.includes(ex)) next = [ex]
        else if (cur.includes(ex) && cur.length > 1) next = cur.filter(v => v !== ex)
        this.lastPicks[p.name] = next
        if (next.length !== cur.length) this.form[p.name] = next
      })
    },
    /** A template rendered for the run on screen, with `row` as its first row. */
    renderForRow(template, row) {
      return renderTemplate(template, {...this.templateContext, row: row || this.rows[0]})
    },
    /** A run parameter as text: its value, or a field of its chosen option. */
    paramText(name, field = null) {
      return renderTemplate('{' + name + (field ? ':' + field : '') + '}', this.templateContext)
    },
    setView(view) {
      this.view = view
      if (view === 'grid') {
        this.$nextTick(this.buildTable)
      }
    },
    /** The params a run sends: the form's, plus those usedBy this run's mode. */
    paramsFor(mode) {
      return this.params.filter(p => !p.usedBy || p.usedBy === mode)
    },
    usedByParams(mode) {
      return this.params.filter(p => p.usedBy === mode)
    },
    run(mode = null) {
      if (!this.definition || !this.definition.implemented) return
      const runParams = this.paramsFor(mode)

      // FrameTracker refuses to print without a required control, and says which.
      const missing = missingRequired(runParams, this.form)
      this.formError = missing.length ? missing[0] : ''
      if (missing.length) return

      this.running = true
      this.destroyTable()

      // Blank means "no filter", a multiple pick goes as one joined string and a
      // hidden param is not sent; see serializeForm() in reportParams.js.
      const params = serializeForm(runParams, this.form)
      if (mode) params.mode = mode
      this.ranWith = {...params}
      this.ranOptions = this.chosenOptions(params)
      this.ranAt = null
      this.sort = {key: null, dir: null}

      this.$store.dispatch('Reports/runReport', {
        slug: this.slug,
        params: params,
        endpoint: this.definition.endpoint || null,
      })
          .then(() => {
            this.ranAt = new Date()
            if (this.view === 'grid') this.$nextTick(this.buildTable)
          })
          .catch(() => { /* message is surfaced through the store's error state */ })
          .finally(() => {
            this.running = false
          })
    },
    buildTable() {
      if (!this.hasResult) return
      this.destroyTable()

      // Displayed with the column's format; sorted on the raw value, so money
      // sorts as a number rather than as '$1,000.00' text.
      const columns = this.gridColumns.map(col => ({
        data: col.key,
        defaultContent: '',
        render: (data, type) => {
          if (type === 'sort' || type === 'type') {
            if (NUMERIC_TYPES.includes(col.type)) {
              const num = parseFloat(data)
              return isNaN(num) ? data : num
            }
            return data
          }
          return this.formatCell(data, col)
        },
      }))

      $('#' + this.tableId).DataTable({
        data: this.rows,
        columns: columns,
        pageLength: 25,
        lengthMenu: [[25, 50, 100, 200, -1], [25, 50, 100, 200, 'All']],
        order: [],
        // Already loaded globally in main.js, so export costs nothing here.
        buttons: this.gridButtons(),
        dom: "<'row'<'col-sm-6'B><'col-sm-6'f>>rtip",
      })

      this.tableBuilt = true
    },
    /**
     * The grid's buttons. A report declaring FrameTracker's export
     * (legacyLayout.export) gets CSV and Excel buttons writing exactly that, plus
     * one button per extra file; any other report keeps DataTables' own.
     */
    gridButtons() {
      // DataTables' own copy / CSV / Excel go through the same formula guard
      // as our export (neutralize in reportExport.js).
      const guarded = extend => ({extend, exportOptions: {format: {body: data => neutralize(data)}}})
      const spec = this.layout && this.layout.export
      if (!spec || !Array.isArray(spec.columns)) {
        return [guarded('copy'), guarded('csv'), guarded('excel'), 'pdf', 'print', 'colvis']
      }
      const extra = (spec.extraFiles || []).map((file, i) => ({
        text: file.name || ('File ' + (i + 1)),
        action: () => this.exportExtra(i),
      }))
      return [
        guarded('copy'),
        {text: 'CSV', action: () => this.exportFile('csv')},
        {text: 'Excel', action: () => this.exportFile('xlsx')},
        ...extra,
        'pdf', 'print', 'colvis',
      ]
    },
    /** One export file: its columns and title rows over the given rows. */
    writeExport({columns, titleRows, rows}, kind, name) {
      const table = buildExportTable({
        columns,
        rows,
        titleRows: renderLines(titleRows || [], this.templateContext),
        types: this.columnsByKey,
      })
      if (kind === 'csv') {
        // The byte-order mark makes Excel read the file as UTF-8.
        download(new Blob(['﻿' + toCsv(table)], {type: 'text/csv;charset=utf-8'}), name + '.csv')
        return Promise.resolve()
      }
      return toXlsx(table, JSZip, name).then(blob => download(blob, name + '.xlsx'))
    },
    exportFile(kind) {
      const spec = this.layout.export
      return this.writeExport({columns: spec.columns, titleRows: spec.titleRows, rows: this.rows}, kind, this.slug)
    },
    /**
     * The Report view's CSV: every row, in the order on screen. FrameTracker's
     * export columns where the report declares them, otherwise the report's own.
     */
    exportReportCsv() {
      const spec = this.layout && this.layout.export
      const columns = spec && Array.isArray(spec.columns)
          ? spec.columns
          : this.gridColumns.map(c => ({key: c.key, label: c.label}))
      const titleRows = spec && Array.isArray(spec.columns) ? spec.titleRows : []
      return this.writeExport({columns, titleRows, rows: this.displayRows}, 'csv', this.slug)
    },
    onSort(key) {
      this.sort = nextSort(this.sort, key)
    },
    /** An extra file (Monthly Invoice's Tax Summary), from a second dataset in meta. */
    exportExtra(index) {
      const file = this.layout.export.extraFiles[index]
      const rows = pathValue({meta: this.meta || {}}, file.source)
      const name = String(file.name || this.slug + '-' + (index + 1)).replace(/[^A-Za-z0-9 _-]+/g, '').trim() || this.slug
      return this.writeExport({columns: file.columns || [], titleRows: file.titleRows, rows: Array.isArray(rows) ? rows : []}, 'xlsx', name)
    },
    destroyTable() {
      // Mandatory: a DataTable left attached breaks on re-entry to the route.
      if (this.tableBuilt && $.fn.DataTable.isDataTable('#' + this.tableId)) {
        $('#' + this.tableId).DataTable().destroy()
        $('#' + this.tableId + ' tbody').empty()
      }
      this.tableBuilt = false
    },
    /**
     * Prints the Report view: every page of it, through the browser's print
     * dialog, with the page items in the margins (printPage.js).
     */
    printReport() {
      const lines = this.rows.length
      if (lines > PRINT_CONFIRM_LINES &&
          !window.confirm('This report has ' + lines.toLocaleString('en-US') + ' lines. Print all of them?')) {
        return
      }
      this.printAll = true
      this.$nextTick(() => {
        window.print()
        this.printAll = false
      })
    },
    usDate(value) {
      if (!value) return ''
      return dateToStr(value)
    },
    pageItemText(corner) {
      const it = this.pageItems.find(i => i.at === corner)
      if (!it) return ''
      if (it.pages !== undefined) {
        if (this.special) return String(it.pages).replace(/\b([nN])\b/g, '1')
        const total = Math.max(1, this.pageInfo.pages)
        return String(it.pages).replace(/\b([nN])\b/g, m => (m === 'n' ? this.pageInfo.page + 1 : total))
      }
      return it.text
    },
    onPages(info) {
      this.pageInfo = info
    },
    formatCell,
    pageItemClass(corner) {
      const it = this.pageItems.find(i => i.at === corner)
      return it ? {'font-weight-bold': it.bold, 'font-italic': it.italic} : {}
    },
    load() {
      this.notFound = false
      this.sort = {key: null, dir: null}
      this.ranWith = {}
      this.ranOptions = {}
      this.ranAt = null
      this.formError = ''

      this.$store.dispatch('Reports/fetchCatalog')
          .then(() => {
            if (!this.definition) {
              this.notFound = true
              return
            }
            this.resetForm()
            this.lastPicks = {}
            this.applyExclusive()
            this.loadLookups()
            this.loadReadouts()
            if (!this.definition.legacyLayout) this.view = 'grid'
          })
          .catch(() => { /* surfaced through the store's error state */ })

      this.$store.dispatch('Reports/clearResult')
    },
  },
  watch: {
    /** Keeps the printed page (margins, running items) in step with the view. */
    printSpec: {
      immediate: true,
      handler(spec) {
        if (spec) setPrintPage(spec)
        else clearPrintPage()
      },
    },
    /**
     * Keep the form on a combination that exists.
     *
     * Options can be conditional (rptCheckRegister's 'CC Summary' layout applies
     * only to the Charge Card report), so switching one radio can strand another
     * on a value that is no longer offered. The legacy form has the same dead
     * combination and silently prints nothing; falling back to the declared
     * default is the recoverable version of that.
     */
    form: {
      deep: true,
      handler() {
        if (this.formError) this.formError = ''
        this.applyExclusive()

        this.params.forEach(param => {
          // A typed custom value is meant to be outside the list.
          if (!param.options || param.allowCustom) return

          const value = this.form[param.name]
          const available = this.optionsFor(param).map(o => o.value)

          // A multiple pick just drops whatever is no longer offered.
          if (param.multiple) {
            if (!Array.isArray(value)) return
            const kept = value.filter(v => available.includes(v))
            if (kept.length !== value.length) this.form[param.name] = kept
            return
          }

          if (value === '' || value === null || available.includes(value)) return

          // Only assign when it actually changes, or this watcher re-triggers itself.
          const blank = isPicker(param) ? null : ''
          const fallback = available.includes(param.default) ? param.default : (available.length ? available[0] : blank)
          if (this.form[param.name] !== fallback) this.form[param.name] = fallback
        })
      },
    },
    // The route can change without remounting when moving between two reports.
    '$route.params.slug'() {
      this.destroyTable()
      this.view = 'report'
      this.load()
    },
  },
  mounted() {
    this.load()
  },
  beforeUnmount() {
    clearPrintPage()
  },
  beforeRouteLeave(to, from, next) {
    this.destroyTable()
    clearPrintPage()
    next()
  },
}
</script>

<style scoped>
/* Deliberately plain and dense, closer to a printed report than to a web table. */
.ft-report {
  background: #fff;
}

/* FrameTracker's page bands on screen: print date and company, User: and page. */
.ft-page-strip {
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 1.25rem;
  font-size: 0.8rem;
  border-bottom: 1px solid #dee2e6;
}

.ft-page-strip span {
  flex: 1;
}

.ft-page-strip span:nth-child(2) {
  text-align: center;
}

.ft-page-strip span:nth-child(3) {
  text-align: right;
}

.ft-page-foot {
  border-bottom: none;
  border-top: 1px solid #dee2e6;
}

/* CharCase = ecUpperCase. */
.report-upper,
.report-upper :deep(input),
.report-upper :deep(.multiselect-single-label),
.report-upper :deep(.multiselect-tag) {
  text-transform: uppercase;
}

.report-check {
  padding-top: 0.45rem;
}

/*
 * Printing: the report alone, no card chrome. AdminLTE already hides the
 * sidebar, header and anything marked .no-print.
 */
@media print {
  .ft-report-card {
    border: none;
    box-shadow: none;
  }

  .ft-report-card .card-body {
    padding: 0;
  }
}

.ft-inline-input {
  width: auto;
}

.report-notes-group + .report-notes-group {
  margin-top: 0.85rem;
}

.report-notes-heading {
  font-weight: bold;
  margin-bottom: 0.3rem;
}

/*
 * Lookup pickers dressed as Bootstrap 4 .form-control: same height, border,
 * focus ring and text color as the date and number boxes beside them, and
 * AdminLTE blue in place of the theme's default green.
 */
.report-picker {
  color: #495057;
  --ms-font-size: 1rem;
  --ms-line-height: 1.5;
  --ms-py: 0.375rem;
  --ms-px: 0.75rem;
  --ms-border-color: #ced4da;
  --ms-border-color-active: #80bdff;
  --ms-radius: 0.25rem;
  --ms-ring-width: 0.2rem;
  --ms-ring-color: rgba(0, 123, 255, 0.25);
  --ms-placeholder-color: #6c757d;
  --ms-tag-bg: #007bff;
  --ms-tag-font-size: 0.875rem;
  --ms-tag-font-weight: 400;
  --ms-tag-my: 0.125rem;
  --ms-tag-py: 0;
  --ms-option-font-size: 0.95rem;
  --ms-option-bg-pointed: #e9ecef;
  --ms-option-color-pointed: #212529;
  --ms-option-bg-selected: #007bff;
  --ms-option-bg-selected-pointed: #0069d9;
  --ms-dropdown-border-color: #ced4da;
}
</style>

<style src="@vueform/multiselect/themes/default.css"></style>
